import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ExternalLink, X, ChevronDown, ChevronRight, Link2, CheckCircle2, CircleAlert, Bug, Loader2 } from 'lucide-react';

import { DUP_LINK_TYPES } from './duplicateLinks';
import { normaliseJiraMarkup } from './jiraMarkup';
import { renderMarkdown } from '../../utils/renderMarkdown';
import RoleGate from '../RoleGate';

const WRITE_ROLES = ['admin', 'write'];

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

function scoreClass(score) {
  if (score >= 0.8) return 'score-high';
  if (score >= 0.7) return 'score-mid';
  return 'score-low';
}

function MatchRow({ match, alreadyLinked, onLink, linking }) {
  const [expanded, setExpanded] = useState(false);

  // Jira wiki constructs are translated to Markdown first — see jiraMarkup.js
  const html = useMemo(
    () => (match.description ? renderMarkdown(normaliseJiraMarkup(match.description)) : null),
    [match.description]
  );

  return (
    <div className={`dup-match-row${alreadyLinked ? ' linked' : ''}`}>
      <div className="dup-match-main">
        <button
          className="dup-match-expand"
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? 'Hide description' : 'Show description'}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <a
          className="dup-match-key"
          href={`${JIRA_BASE}/${match.key}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink size={13} />
          {match.key}
        </a>

        <span className="dup-match-summary" title={match.summary}>{match.summary}</span>

        <span className={`dup-score-pill ${scoreClass(match.score)}`}>{match.score.toFixed(3)}</span>

        <span className="dup-match-meta">
          {match.status && <span className="status-chip default">{match.status}</span>}
          {match.resolution && <span className="status-chip resolution-unresolved">{match.resolution}</span>}
        </span>

        {alreadyLinked ? (
          <span className="dup-linked-badge"><CheckCircle2 size={13} /> Linked</span>
        ) : (
          <RoleGate
            allow={WRITE_ROLES}
            fallback={
              <button className="dup-link-btn" disabled title="Requires write access to link duplicates">
                <Link2 size={13} /> Mark as duplicate
              </button>
            }
          >
            <button className="dup-link-btn" onClick={() => onLink(match.key)} disabled={linking}>
              <Link2 size={13} /> {linking ? 'Linking…' : 'Mark as duplicate'}
            </button>
          </RoleGate>
        )}
      </div>

      {expanded && (
        <div className="dup-match-description">
          {html
            ? <div className="dup-md" dangerouslySetInnerHTML={{ __html: html }} />
            : <span className="dup-muted">No description.</span>}
        </div>
      )}
    </div>
  );
}

export default function MatchesDialog({ bug, onClose, onLink, linking, candidates, candidatesLoading, candidatesError }) {
  const [manualKey, setManualKey] = useState('');
  const overlayRef = useRef(null);

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!bug) return null;

  // candidates (GET /hub/duplicates/:key) is the complete, description-including set for this
  // one bug, fetched on demand — bug.matches/bug.duplicateLinks (from the lightweight list
  // response) are only a fallback for the instant between the dialog opening and that request
  // resolving.
  const matches = candidates?.matches ?? bug.matches;
  const duplicateLinks = candidates?.duplicateLinks ?? bug.duplicateLinks;

  const linkedKeys = new Set(
    (duplicateLinks ?? [])
      .filter(l => DUP_LINK_TYPES.includes(l.link))
      .map(l => l.linkedIssue)
  );

  const trimmedManual = manualKey.trim().toUpperCase();
  const manualValid = /^[A-Z][A-Z0-9_]+-\d+$/.test(trimmedManual);

  function submitManual(e) {
    e.preventDefault();
    if (!manualValid) return;
    onLink(trimmedManual);
    setManualKey('');
  }

  return (
    <div
      className="dup-dialog-overlay"
      ref={overlayRef}
      onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="dup-dialog" role="dialog" aria-modal="true" aria-label="Potential duplicates">
        <div className="dup-dialog-header">
          <div className="dup-dialog-title">
            <span className="issue-type-pill bug"><Bug size={11} />Bug</span>
            <a
              className="dup-match-key"
              href={`${JIRA_BASE}/${bug.key}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={13} />
              {bug.key}
            </a>
            <span className="dup-dialog-summary">{bug.summary}</span>
          </div>
          <button className="column-selector-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="dup-dialog-note">
          <CircleAlert size={13} />
          Linking creates a real <code>Duplicate</code> link in Jira from <strong>{bug.key}</strong>
          {' '}to the ticket you pick, and leaves an audit comment on {bug.key} recording who did it.
        </div>

        <div className="dup-dialog-body">
          {candidatesLoading && !candidates ? (
            <div className="dup-muted" style={{ padding: '12px 2px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={14} className="dup-spin" /> Loading candidates…
            </div>
          ) : candidatesError ? (
            <div className="dup-muted" style={{ padding: '12px 2px' }}>
              Couldn't load duplicate candidates for {bug.key}. Link one manually below.
            </div>
          ) : matches.length === 0 ? (
            <div className="dup-muted" style={{ padding: '12px 2px' }}>
              No matches found for this bug. Link one manually below.
            </div>
          ) : (
            <>
              <div className="dup-dialog-section-label">
                {matches.length} potential match{matches.length !== 1 ? 'es' : ''}
              </div>
              {matches.map(m => (
                <MatchRow
                  key={m.key}
                  match={m}
                  alreadyLinked={linkedKeys.has(m.key)}
                  onLink={onLink}
                  linking={linking}
                />
              ))}
            </>
          )}
        </div>

        <RoleGate
          allow={WRITE_ROLES}
          fallback={
            <div className="dup-dialog-manual" title="Requires write access to link duplicates">
              <label htmlFor="dup-manual-key">Or link to a ticket not listed</label>
              <div className="dup-manual-row">
                <input
                  id="dup-manual-key"
                  type="text"
                  placeholder="e.g. QA-1234"
                  className="issues-search-input"
                  disabled
                />
                <button type="button" className="dup-link-btn" disabled>
                  <Link2 size={13} /> Link
                </button>
              </div>
            </div>
          }
        >
          <form className="dup-dialog-manual" onSubmit={submitManual}>
            <label htmlFor="dup-manual-key">Or link to a ticket not listed</label>
            <div className="dup-manual-row">
              <input
                id="dup-manual-key"
                type="text"
                placeholder="e.g. QA-1234"
                value={manualKey}
                onChange={e => setManualKey(e.target.value)}
                className="issues-search-input"
              />
              <button type="submit" className="dup-link-btn" disabled={!manualValid || linking}>
                <Link2 size={13} /> {linking ? 'Linking…' : 'Link'}
              </button>
            </div>
            {manualKey.trim() && !manualValid && (
              <span className="dup-manual-error">Expected a Jira key like PROJ-123.</span>
            )}
          </form>
        </RoleGate>
      </div>
    </div>
  );
}
